import { memo, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { Menu } from 'react-native-paper'
import {
    useJobsSearchQuery,
    useJobsStatusFilter,
    useSetJobsSearchQuery,
    useSetJobsStatusFilter,
} from '@/stores/jobs-filter-store'
import { getJobStatusDisplay, JOB_STATUS_ORDER } from '@/types/jobs'
import { Button } from '@/ui/Button'
import { DebouncedSearchbar } from '@/ui/DebouncedSearchbar'

interface JobsListHeaderProps {
    onExport?: () => void
    isExporting?: boolean
    canExport?: boolean
}

export const JobsListHeader = memo(function JobsListHeader({
    onExport,
    isExporting,
    canExport,
}: JobsListHeaderProps) {
    const searchQuery = useJobsSearchQuery()
    const statusFilter = useJobsStatusFilter()
    const setSearchQuery = useSetJobsSearchQuery()
    const setStatusFilter = useSetJobsStatusFilter()

    const [menuVisible, setMenuVisible] = useState(false)
    const menuKeyRef = useRef(0)

    const openMenu = () => setMenuVisible(true)

    const closeMenu = () => {
        menuKeyRef.current += 1
        setMenuVisible(false)
    }

    const getButtonLabel = () => {
        if (statusFilter === null) return 'All'
        return getJobStatusDisplay(statusFilter)
    }

    return (
        <View style={styles.container}>
            <View style={styles.row}>
                <DebouncedSearchbar
                    placeholder="Search jobs..."
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                />
                <Menu
                    key={menuKeyRef.current}
                    visible={menuVisible}
                    onDismiss={closeMenu}
                    anchor={
                        <Button
                            title={getButtonLabel()}
                            mode="outlined"
                            onPress={openMenu}
                            icon="chevron-down"
                            style={styles.statusButton}
                            compact
                        />
                    }
                >
                    <Menu.Item
                        onPress={() => {
                            setStatusFilter(null)
                            closeMenu()
                        }}
                        title="All"
                        leadingIcon={statusFilter === null ? 'check' : undefined}
                    />
                    {JOB_STATUS_ORDER.map(status => (
                        <Menu.Item
                            key={status}
                            onPress={() => {
                                setStatusFilter(status)
                                closeMenu()
                            }}
                            title={getJobStatusDisplay(status)}
                            leadingIcon={statusFilter === status ? 'check' : undefined}
                        />
                    ))}
                </Menu>
                {onExport && (
                    <Button
                        title="Export"
                        mode="outlined"
                        icon="download"
                        onPress={onExport}
                        loading={isExporting}
                        disabled={isExporting || !canExport}
                        compact
                    />
                )}
            </View>
        </View>
    )
})

const styles = StyleSheet.create({
    container: {
        paddingTop: 16,
        paddingBottom: 8,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    statusButton: {
        minWidth: 100,
    },
})
